import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	ASSET_STATUSES,
	ASSET_TYPES,
	BLOCK_ON_FAMILIES,
	INSPECTION_TYPES,
	type LibraryDefaults,
	type LibraryMetadataInput,
} from "@/types/api/checklist-library";
import { useId } from "react";
import * as z from "zod";
import { emptyToUndefined } from "./checklist-library-utils";

/** Copy-down defaults + asset class as one form value (controlled). */
export const libraryDefaultsSchema = z.object({
	assetClassKey: z.string().max(100, "At most 100 characters").optional(),
	defaultAssetType: z.string().optional(),
	defaultAssetGroupName: z.string().optional(),
	defaultUserGroupName: z.string().optional(),
	defaultInspectionType: z.string().optional(),
	defaultPassAssetStatus: z.string().optional(),
	defaultFlagAssetStatus: z.string().optional(),
	defaultFailAssetStatus: z.string().optional(),
	defaultAllowMultipleSameDay: z.boolean(),
	defaultBlockOnAssetStatusFamilies: z.array(z.string()),
});

export type LibraryDefaultsFormValues = z.infer<typeof libraryDefaultsSchema>;

/** Prefills for a new catalog item (the starter-pack manifest convention). */
export const PUBLISH_DEFAULT_VALUES: LibraryDefaultsFormValues = {
	assetClassKey: "",
	defaultAssetType: "Equipment",
	defaultAssetGroupName: "",
	defaultUserGroupName: "Inspectors",
	defaultInspectionType: "OnDemand",
	defaultPassAssetStatus: "Operational",
	defaultFlagAssetStatus: "NeedsAttention",
	defaultFailAssetStatus: "OutOfService",
	defaultAllowMultipleSameDay: true,
	defaultBlockOnAssetStatusFamilies: ["Bad"],
};

export function defaultsFromApi(
	defaults: LibraryDefaults | undefined,
	assetClassKey: string | null | undefined,
): LibraryDefaultsFormValues {
	return {
		assetClassKey: assetClassKey ?? "",
		defaultAssetType: defaults?.defaultAssetType ?? "",
		defaultAssetGroupName: defaults?.defaultAssetGroupName ?? "",
		defaultUserGroupName: defaults?.defaultUserGroupName ?? "",
		defaultInspectionType: defaults?.defaultInspectionType ?? "",
		defaultPassAssetStatus: defaults?.defaultPassAssetStatus ?? "",
		defaultFlagAssetStatus: defaults?.defaultFlagAssetStatus ?? "",
		defaultFailAssetStatus: defaults?.defaultFailAssetStatus ?? "",
		defaultAllowMultipleSameDay:
			defaults?.defaultAllowMultipleSameDay ?? true,
		defaultBlockOnAssetStatusFamilies:
			defaults?.defaultBlockOnAssetStatusFamilies ?? [],
	};
}

/** Form values → request fields (empty selections are omitted = unchanged). */
export function defaultsToRequest(
	values: LibraryDefaultsFormValues,
): Pick<
	LibraryMetadataInput,
	| "assetClassKey"
	| "defaultAssetType"
	| "defaultAssetGroupName"
	| "defaultUserGroupName"
	| "defaultInspectionType"
	| "defaultPassAssetStatus"
	| "defaultFlagAssetStatus"
	| "defaultFailAssetStatus"
	| "defaultAllowMultipleSameDay"
	| "defaultBlockOnAssetStatusFamilies"
> {
	return {
		assetClassKey: emptyToUndefined(values.assetClassKey),
		defaultAssetType: emptyToUndefined(values.defaultAssetType),
		defaultAssetGroupName: emptyToUndefined(values.defaultAssetGroupName),
		defaultUserGroupName: emptyToUndefined(values.defaultUserGroupName),
		defaultInspectionType: emptyToUndefined(values.defaultInspectionType),
		defaultPassAssetStatus: emptyToUndefined(values.defaultPassAssetStatus),
		defaultFlagAssetStatus: emptyToUndefined(values.defaultFlagAssetStatus),
		defaultFailAssetStatus: emptyToUndefined(values.defaultFailAssetStatus),
		defaultAllowMultipleSameDay: values.defaultAllowMultipleSameDay,
		defaultBlockOnAssetStatusFamilies:
			values.defaultBlockOnAssetStatusFamilies,
	};
}

/** Radix Select forbids an empty item value; this stands in for "not set". */
const UNSET = "__unset__";

interface EnumSelectProps {
	id: string;
	value: string | undefined;
	options: readonly string[];
	onChange: (next: string) => void;
	disabled?: boolean;
	allowUnset?: boolean;
}

function EnumSelect({
	id,
	value,
	options,
	onChange,
	disabled,
	allowUnset,
}: EnumSelectProps) {
	return (
		<Select
			value={value ? value : UNSET}
			onValueChange={(next) => onChange(next === UNSET ? "" : next)}
			disabled={disabled}
		>
			<SelectTrigger id={id}>
				<SelectValue placeholder="Not set" />
			</SelectTrigger>
			<SelectContent>
				{(allowUnset || !value) && (
					<SelectItem value={UNSET}>Not set</SelectItem>
				)}
				{options.map((option) => (
					<SelectItem key={option} value={option}>
						{option}
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	);
}

interface LibraryDefaultsFieldsProps {
	value: LibraryDefaultsFormValues;
	onChange: (next: LibraryDefaultsFormValues) => void;
	disabled?: boolean;
	/** Offer "Not set" on every select (metadata edit); publish prefills all of them. */
	allowUnset?: boolean;
}

/**
 * The wiring a copy-down uses when `wireInspection` is on: which asset
 * group / user group it get-or-creates, the inspection type and the asset
 * statuses a pass / flag / fail outcome sets.
 */
export function LibraryDefaultsFields({
	value,
	onChange,
	disabled,
	allowUnset,
}: LibraryDefaultsFieldsProps) {
	const id = useId();
	const set = <K extends keyof LibraryDefaultsFormValues>(
		key: K,
		next: LibraryDefaultsFormValues[K],
	) => onChange({ ...value, [key]: next });

	const toggleFamily = (family: string, checked: boolean) => {
		const current = value.defaultBlockOnAssetStatusFamilies;
		set(
			"defaultBlockOnAssetStatusFamilies",
			checked
				? current.includes(family)
					? current
					: [...current, family]
				: current.filter((f) => f !== family),
		);
	};

	return (
		<div className="space-y-4">
			<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
				<div className="space-y-1.5">
					<Label htmlFor={`${id}-assetClassKey`}>
						Asset class key
					</Label>
					<Input
						id={`${id}-assetClassKey`}
						value={value.assetClassKey ?? ""}
						onChange={(e) => set("assetClassKey", e.target.value)}
						placeholder="e.g. forklift"
						maxLength={100}
						disabled={disabled}
					/>
					<p className="text-xs text-slate-500">
						Picks the asset group for imported assets of this class.
					</p>
				</div>
				<div className="space-y-1.5">
					<Label htmlFor={`${id}-assetType`}>Asset type</Label>
					<EnumSelect
						id={`${id}-assetType`}
						value={value.defaultAssetType}
						options={ASSET_TYPES}
						onChange={(next) => set("defaultAssetType", next)}
						disabled={disabled}
						allowUnset={allowUnset}
					/>
				</div>
				<div className="space-y-1.5">
					<Label htmlFor={`${id}-assetGroupName`}>
						Asset group name
					</Label>
					<Input
						id={`${id}-assetGroupName`}
						value={value.defaultAssetGroupName ?? ""}
						onChange={(e) =>
							set("defaultAssetGroupName", e.target.value)
						}
						placeholder="e.g. Forklifts"
						disabled={disabled}
					/>
				</div>
				<div className="space-y-1.5">
					<Label htmlFor={`${id}-userGroupName`}>
						Inspector user group name
					</Label>
					<Input
						id={`${id}-userGroupName`}
						value={value.defaultUserGroupName ?? ""}
						onChange={(e) =>
							set("defaultUserGroupName", e.target.value)
						}
						placeholder="e.g. Inspectors"
						disabled={disabled}
					/>
				</div>
				<div className="space-y-1.5">
					<Label htmlFor={`${id}-inspectionType`}>
						Inspection type
					</Label>
					<EnumSelect
						id={`${id}-inspectionType`}
						value={value.defaultInspectionType}
						options={INSPECTION_TYPES}
						onChange={(next) => set("defaultInspectionType", next)}
						disabled={disabled}
						allowUnset={allowUnset}
					/>
				</div>
				<div className="flex items-center gap-2 sm:pt-7">
					<Checkbox
						id={`${id}-allowMultiple`}
						checked={value.defaultAllowMultipleSameDay}
						onCheckedChange={(checked) =>
							set("defaultAllowMultipleSameDay", checked === true)
						}
						disabled={disabled}
					/>
					<Label
						htmlFor={`${id}-allowMultiple`}
						className="font-normal"
					>
						Allow multiple inspections per day
					</Label>
				</div>
			</div>

			<div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
				<div className="space-y-1.5">
					<Label htmlFor={`${id}-pass`}>Pass sets status</Label>
					<EnumSelect
						id={`${id}-pass`}
						value={value.defaultPassAssetStatus}
						options={ASSET_STATUSES}
						onChange={(next) => set("defaultPassAssetStatus", next)}
						disabled={disabled}
						allowUnset={allowUnset}
					/>
				</div>
				<div className="space-y-1.5">
					<Label htmlFor={`${id}-flag`}>Flag sets status</Label>
					<EnumSelect
						id={`${id}-flag`}
						value={value.defaultFlagAssetStatus}
						options={ASSET_STATUSES}
						onChange={(next) => set("defaultFlagAssetStatus", next)}
						disabled={disabled}
						allowUnset={allowUnset}
					/>
				</div>
				<div className="space-y-1.5">
					<Label htmlFor={`${id}-fail`}>Fail sets status</Label>
					<EnumSelect
						id={`${id}-fail`}
						value={value.defaultFailAssetStatus}
						options={ASSET_STATUSES}
						onChange={(next) => set("defaultFailAssetStatus", next)}
						disabled={disabled}
						allowUnset={allowUnset}
					/>
				</div>
			</div>

			<div className="space-y-1.5">
				<span className="text-sm font-medium leading-none">
					Block new inspections while the asset status family is
				</span>
				<div className="flex items-center gap-6 pt-1">
					{BLOCK_ON_FAMILIES.map((family) => (
						<div key={family} className="flex items-center gap-2">
							<Checkbox
								id={`${id}-family-${family}`}
								checked={value.defaultBlockOnAssetStatusFamilies.includes(
									family,
								)}
								onCheckedChange={(checked) =>
									toggleFamily(family, checked === true)
								}
								disabled={disabled}
							/>
							<Label
								htmlFor={`${id}-family-${family}`}
								className="font-normal"
							>
								{family}
							</Label>
						</div>
					))}
				</div>
			</div>
		</div>
	);
}
